import dateformat from 'dateformat';
import {
  DATE_FORMAT_LONG,
  endOfDay,
  getStartOfWeek,
  startOfDay,
} from 'dateUtils';
import {
  USAGE_REPORT_PERIOD,
  UsageReportDateRange,
  UsageReportPeriod,
} from 'models/EnterprisesAnalytics';

export const getUsageReportDateFormat = (date: Date) =>
  dateformat(date, DATE_FORMAT_LONG);

const getSevenDayRangeEndingBefore = (
  rangeStart: Date,
): UsageReportDateRange => {
  const end = new Date(rangeStart);
  end.setDate(end.getDate() - 1);

  const start = new Date(end);
  start.setDate(start.getDate() - 6);

  return { start: startOfDay(start), end: endOfDay(end) };
};

const getMonthRange = (
  year: number,
  month: number,
  monthCount = 1,
): UsageReportDateRange => ({
  start: startOfDay(new Date(year, month, 1)),
  end: endOfDay(new Date(year, month + monthCount, 0)),
});

const getSevenDayRangeEndingOn = (rangeEnd: Date): UsageReportDateRange => {
  const start = new Date(rangeEnd);
  start.setDate(start.getDate() - 6);

  return { start: startOfDay(start), end: endOfDay(rangeEnd) };
};

export const getUsageReportRange = (
  period: UsageReportPeriod,
  now: Date,
  earliestRecordDate: Date,
) => {
  switch (period) {
    case USAGE_REPORT_PERIOD.LAST_WEEK: {
      const range = getSevenDayRangeEndingBefore(getStartOfWeek(now));

      return {
        range,
        comparisonRange: getSevenDayRangeEndingBefore(range.start),
      };
    }
    case USAGE_REPORT_PERIOD.LAST_7_DAYS: {
      const range = getSevenDayRangeEndingOn(now);

      return {
        range,
        comparisonRange: getSevenDayRangeEndingBefore(range.start),
      };
    }
    case USAGE_REPORT_PERIOD.LAST_MONTH: {
      const range = getMonthRange(now.getFullYear(), now.getMonth() - 1);

      return {
        range,
        comparisonRange: getMonthRange(
          range.start.getFullYear(),
          range.start.getMonth() - 1,
        ),
      };
    }
    case USAGE_REPORT_PERIOD.LAST_FULL_QUARTER: {
      const currentQuarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      const range = getMonthRange(
        now.getFullYear(),
        currentQuarterStartMonth - 3,
        3,
      );

      return {
        range,
        comparisonRange: getMonthRange(
          range.start.getFullYear(),
          range.start.getMonth() - 3,
          3,
        ),
      };
    }
    case USAGE_REPORT_PERIOD.ALL_TIME:
    default:
      return { range: { start: earliestRecordDate, end: now } };
  }
};
